import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Filter, Loader2 } from 'lucide-react';
import { GameCategory, GAME_CATEGORIES, getCategoryDisplayName } from '../types/game';
import { useGamesStore } from '../store/useGamesStore';
import { gamesService } from '../services/gamesService';
import GameCard from '../components/game/GameCard';
import GameGrid from '../components/game/GameGrid';
import type { Game } from '../types/game';

// Get display categories (exclude 'All' for filter checkboxes)
const filterCategories = GAME_CATEGORIES.filter(c => c.id !== 'All');

export default function SearchPage() {
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  
  const [selectedCategories, setSelectedCategories] = useState<Set<GameCategory>>(new Set());
  const [sortBy, setSortBy] = useState<'popular' | 'recent'>('popular');
  const [searchResults, setSearchResults] = useState<Game[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  
  const { games, fetchCatalog, hasLoadedCatalog, isLoadingCatalog } = useGamesStore();

  // Fetch catalog if not loaded
  useEffect(() => {
    if (!hasLoadedCatalog) {
      fetchCatalog();
    }
  }, [hasLoadedCatalog, fetchCatalog]);

  // Search with debounce
  useEffect(() => {
    const performSearch = async () => {
      setIsSearching(true);
      setSearchError(null);
      
      try {
        if (query) {
          const results = await gamesService.searchGames(query, 50);
          setSearchResults(results);
        } else {
          // No query, use cached catalog
          setSearchResults(games);
        }
      } catch (error: any) {
        console.error('Search failed:', error);
        setSearchError(error.message || 'Search failed');
        // Fallback to local filtering if API fails
        if (query) {
          const lowerQuery = query.toLowerCase();
          const filtered = games.filter(game =>
            game.name.toLowerCase().includes(lowerQuery) ||
            game.description?.toLowerCase().includes(lowerQuery) ||
            game.tags?.some(tag => tag.toLowerCase().includes(lowerQuery))
          );
          setSearchResults(filtered);
        }
      } finally {
        setIsSearching(false);
      }
    };

    // Debounce search by 300ms
    const timeoutId = setTimeout(performSearch, 300);
    return () => clearTimeout(timeoutId);
  }, [query, games]);

  // Filter and sort games
  const getFilteredGames = useCallback(() => {
    let filtered = searchResults;
    
    // Filter by categories
    if (selectedCategories.size > 0) {
      filtered = filtered.filter(game => 
        selectedCategories.has(game.category?.toLowerCase() as GameCategory)
      );
    }
    
    // Sort
    switch (sortBy) {
      case 'popular':
        filtered = [...filtered].sort((a, b) => (b.total_plays || 0) - (a.total_plays || 0));
        break;
      case 'recent':
        filtered = [...filtered].sort((a, b) => 
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
        break;
    }
    
    return filtered;
  }, [searchResults, selectedCategories, sortBy]);
  
  const filteredGames = getFilteredGames();
  
  const toggleCategory = (category: GameCategory) => {
    const newSelected = new Set(selectedCategories);
    if (newSelected.has(category)) {
      newSelected.delete(category);
    } else {
      newSelected.add(category);
    }
    setSelectedCategories(newSelected);
  };

  const clearFilters = () => {
    setSelectedCategories(new Set());
  };

  // Loading skeleton
  const GameCardSkeleton = () => (
    <div className="card overflow-hidden animate-pulse">
      <div className="aspect-[3/4] bg-gray-200 dark:bg-gray-700" />
      <div className="p-4 space-y-2">
        <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3" />
      </div>
    </div>
  );

  const isLoading = isSearching || isLoadingCatalog;

  return (
    <div className="grid lg:grid-cols-[280px_1fr] gap-8">
      {/* Filters Sidebar */}
      <aside className="space-y-6">
        <div className="card p-6 sticky top-20">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-5 h-5 text-primary-500" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Filters</h2>
          </div>

          {/* Categories */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold mb-3 text-gray-900 dark:text-white">Categories</h3>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {filterCategories.map((category) => (
                <label key={category.id} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={selectedCategories.has(category.id)}
                    onChange={() => toggleCategory(category.id)}
                    className="rounded border-gray-300 text-primary-500 focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">{category.displayName}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Clear Filters */}
          <button
            onClick={clearFilters}
            className="w-full btn-secondary text-sm"
          >
            Clear All
          </button>
        </div>
      </aside>

      {/* Results */}
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold mb-1 text-gray-900 dark:text-white">
              {query ? `Search results for "${query}"` : 'All Games'}
            </h1>
            <p className="text-muted">
              {isLoading ? 'Searching...' : `${filteredGames.length} ${filteredGames.length === 1 ? 'game' : 'games'} found`}
            </p>
            {searchError && (
              <p className="text-sm text-yellow-600 dark:text-yellow-400 mt-1">
                Using local search (API unavailable)
              </p>
            )}
          </div>

          {/* Sort */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted">Sort by:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'popular' | 'recent')}
              className="input py-1 px-2 pr-8 text-sm"
            >
              <option value="popular">Popular</option>
              <option value="recent">Recent</option>
            </select>
          </div>
        </div>

        {/* Games Grid */}
        {isLoading ? (
          <GameGrid>
            {Array.from({ length: 8 }).map((_, i) => (
              <GameCardSkeleton key={i} />
            ))}
          </GameGrid>
        ) : filteredGames.length > 0 ? (
          <GameGrid>
            {filteredGames.map((game) => (
              <GameCard key={game.id} game={game} />
            ))}
          </GameGrid>
        ) : (
          <div className="text-center py-12">
            <p className="text-lg text-muted">No games found matching your filters</p>
            <button
              onClick={clearFilters}
              className="mt-4 btn-primary"
            >
              Clear Filters
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
